/**
 * 契約者向けの書式。
 *
 * 汎用の `formatBytes` / `formatDateTime` / `formatCount` などは
 * `@mail-sv/ui` にあるので、ここでは **契約者パネル固有の言い回し**だけを持つ。
 *
 * **3 桁区切りを自分で書かない。** 以前は `toLocaleString('ja-JP')` が
 * 画面側に 30 か所以上散らばっていて、`null` / `NaN` の扱い（`—` を出すか
 * `0` を出すか）が場所ごとに違っていた。数の整形は必ず `formatCount()` を通す。
 */
import { formatCount } from '@mail-sv/ui';

/** 金額（最小通貨単位＝ JPY なら円）を「1,500 円」で表す。 */
export function formatMoney(amount: number, currency = 'JPY'): string {
  if (currency === 'JPY') return `${formatCount(amount)} 円`;
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
}

/** プラン上限の 0 は「無制限」。表の中で 0 と書くと誤解される。 */
export function formatLimit(value: number, unit = ''): string {
  if (value === 0) return '無制限';
  return `${formatCount(value)}${unit}`;
}

/** 容量上限。0（無制限）を許すのは enterprise だけ。 */
export function formatQuota(bytes: number): string {
  if (bytes === 0) return '無制限';
  return formatGiB(bytes);
}

const GiB = 1024 ** 3;
const MiB = 1024 ** 2;

export function formatGiB(bytes: number): string {
  if (bytes >= GiB) {
    const v = bytes / GiB;
    return `${v >= 100 ? Math.round(v) : Number(v.toFixed(1))} GB`;
  }
  return `${Math.max(1, Math.round(bytes / MiB))} MB`;
}

/** GB 表記 → バイト。入力欄で GB を受け取るときに使う。 */
export function gibToBytes(gib: number): number {
  return Math.round(gib * GiB);
}

export function bytesToGib(bytes: number): number {
  return Math.round((bytes / GiB) * 10) / 10;
}

/** 「3 分前」。最終チェック時刻の表示に使う。 */
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '未実施';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '未実施';
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return 'たった今';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分前`;
  if (diffSec < 86_400) return `${Math.floor(diffSec / 3600)} 時間前`;
  return `${Math.floor(diffSec / 86_400)} 日前`;
}

/** 日付だけ（請求期間など）。 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** 使用率（0..1）。総量 0 = 無制限のときは null（バーを出さない）。 */
export function usageRatio(used: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.min(1, used / total);
}
